import { useEffect, useState } from 'react';
import { Modal, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Field, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { TextBox } from '../../../shared/controls/form';
import PasswordBox from '../../../shared/controls/form/passwordBox';
import apiServices from '../../../services/service';
import { getUser } from '../../../redux/selector';
import { useSelector } from 'react-redux';
// import "bootstrap/dist/css/bootstrap.min.css";

/* ── Password Strength Helper ── */
function getStrength(password) {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const map = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  return { score, label: map[score] };
}

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const getValidationSchema = (changePasswordAdmin) =>
  Yup.object({
    ...(!changePasswordAdmin && {
      currentPassword: Yup.string().required('Current password is required'),
    }),

    newPassword: Yup.string()
      .min(8, 'At least 8 characters required')
      .matches(/[A-Z]/, 'Must include uppercase')
      .matches(/[0-9]/, 'Must include number')
      .matches(/[^A-Za-z0-9]/, 'Must include special character')
      .required('New password required'),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
      .required('Confirm password required'),
  });

/* ── Main Modal ── */
export default function ChangePasswordModal({ show, onHide, selectedUser, changePasswordAdmin }) {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const userInfo = useSelector(getUser);

  const handleClose = () => {
  onHide();
};
useEffect(() => {
  if (!show) {
    setSuccess(false);
  }
}, [show]);

  return (
    <>
      {/* <style>{styles}</style> */}
      <Modal show={show} onHide={handleClose} centered className="cp-modal" backdrop="static">
        <Modal.Header>
          <div className="cp-header-content">
            <div className="cp-header-icon">
              <LockIcon />
            </div>
            <div className="cp-header-text">
              <h5>Change Password</h5>
              <p>Keep your account secure</p>
            </div>
          </div>

          {/* Custom Close Button */}
          <button type="button" className="cp-close-btn" onClick={handleClose} aria-label="Close">
            <i className="bi bi-x-lg"></i>
          </button>
        </Modal.Header>

        {success ? (
          <>
            <Modal.Body className="p-0">
              <div className="cp-success-body">
                <div className="cp-success-icon">
                  <CheckIcon />
                </div>
                <div className="cp-success-title">Password Updated!</div>
                <div className="cp-success-sub">Your password has been changed successfully.</div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button className="cp-btn-submit w-100" onClick={handleClose}>
                Done
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <Formik
            initialValues={{
              currentPassword: '',
              newPassword: '',
              confirmPassword: '',
              reason: '',
            }}
            context={{ changePasswordAdmin }} // 🔥 THIS IS REQUIRED
            validationSchema={getValidationSchema(changePasswordAdmin)}
            onSubmit={async (values, { setSubmitting, resetForm, setErrors }) => {
              try {
                setSubmitting(true);
                const payload = {
                  userId: changePasswordAdmin ? selectedUser.userId : userInfo?.user?.userId,

                  newPassword: values.newPassword,
                  updateReason: values.reason,
                };

                if (!changePasswordAdmin) {
                  payload.oldPassword = values.currentPassword;
                }

                const response = await apiServices.updatePassword(payload);

                if (response?.status == 200) {
                  resetForm();
                  setSuccess(true);
                } else {
                  setErrors({
                    currentPassword: response?.message || 'Password update failed',
                  });
                }
              } catch (error) {
                setErrors({
                  currentPassword:
                    error?.response?.data?.message || 'Something went wrong. Please try again.',
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              isValid,
              dirty,
              handleChange,
              handleSubmit,
            }) => {
              const strength = getStrength(values.newPassword);
              const strengthColors = ['', 'active-1', 'active-2', 'active-3', 'active-4'];

              return (
                <Form onSubmit={handleSubmit}>
                  <Modal.Body>
                    {/* Current Password */}
                    {!changePasswordAdmin && (
                      <PasswordBox
                        label="Current Password"
                        name="currentPassword"
                        placeholder="Enter Current Password"
                        value={values.currentPassword}
                        handleChange={handleChange}
                        errormsg={touched.currentPassword && errors.currentPassword}
                        isRequired
                      />
                    )}
                    {!changePasswordAdmin && <div className="cp-divider" />}
                    {/* New Password */}
                    <PasswordBox
                      label="New Password"
                      name="newPassword"
                      placeholder="Enter New Password"
                      value={values.newPassword}
                      handleChange={handleChange}
                      errormsg={touched.newPassword && errors.newPassword}
                      isRequired
                    />

                    {/* Strength Meter */}
                    {values.newPassword && (
                      <div className="cp-strength-wrap">
                        <div className="cp-strength-label">
                          <span>Password strength</span>
                          <span
                            className="level"
                            style={{
                              color: ['', '#ef4444', '#f97316', '#eab308', '#10b981'][
                                strength.score
                              ],
                            }}
                          >
                            {strength.label}
                          </span>
                        </div>

                        <div className="cp-strength-bar-track">
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className={`cp-strength-segment ${
                                i <= strength.score ? strengthColors[strength.score] : ''
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Confirm Password */}
                    <PasswordBox
                      label="Confirm New Password"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={values.confirmPassword}
                      handleChange={handleChange}
                      errormsg={touched.confirmPassword && errors.confirmPassword}
                      isRequired
                    />

                    <div className="cp-divider" />

                    {/* Reason */}
                    <div>
                      <div className="cp-field-label">Reason To Change Password</div>
                      <div className="cp-input-wrap">
                        <Field
                          as="textarea"
                          name="reason"
                          placeholder="Enter Reason"
                          className="form-control"
                          // rows={5}
                        />
                      </div>
                    </div>
                  </Modal.Body>

                  {/* FIXED FOOTER */}
                  <Modal.Footer>
                    <Button
                      type="button"
                      className="cp-btn-cancel"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>

                    <button
                      type="submit"
                      className="cp-btn-submit"
                      disabled={isSubmitting || !isValid || (!dirty && !changePasswordAdmin)}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <LockIcon />
                          Update Password
                        </>
                      )}
                    </button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
}

// /* ── Demo wrapper ── */
// export function App() {
//   const [show, setShow] = useState(false);
//   return (
//     <div style={{ minHeight: "100vh", background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Nunito, sans-serif" }}>
//       <button
//         onClick={() => setShow(true)}
//         style={{
//           padding: "12px 28px",
//           borderRadius: "12px",
//           background: "linear-gradient(135deg, #6d28d9, #8b5cf6)",
//           color: "white",
//           border: "none",
//           fontFamily: "Nunito, sans-serif",
//           fontSize: "15px",
//           fontWeight: "800",
//           cursor: "pointer",
//           boxShadow: "0 4px 14px rgba(109,40,217,0.35)",
//         }}
//       >
//         🔒 Change Password
//       </button>
//       <ChangePasswordModal show={show} onHide={() => setShow(false)} />
//     </div>
//   );
// }
